import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * Lightweight Pinia store that carries state across the parent-side
 * "Rejoindre une famille" multi-page flow:
 *   /join/code -> /join/preview -> /join/auth -> /join/role -> /join/success
 *
 * Pages don't pass query params; they read/write here. The store is
 * reset() from /join/code so each attempt starts clean.
 */

export interface JoinHouseholdPreview {
  id: string
  name: string
  ownerName?: string
  childrenCount?: number
  tasksCount?: number
}

/**
 * Known error kinds surfaced by the join flow. Anything else falls into
 * 'unknown' (rendered as ERR-Invalid by default).
 */
export type JoinFlowErrorKind =
  | 'invalid'         // code unknown / format wrong
  | 'expired'         // code is past expires_at
  | 'used'            // already accepted by another user
  | 'already_member'  // THIS user already belongs to the target household
  | 'premium_required' // le foyer ne porte qu'un parent sans Harmonia+
  | 'rate_limited'    // trop de vérifications ratées, le garde a fermé
  | 'connection'      // network down / fetch failed
  | 'unknown'         // other server error

export interface JoinFlowError {
  kind: JoinFlowErrorKind
  /** Optional: who used the code (only for 'used' when surfaced by RPC). */
  usedBy?: string
  /** Raw error message for debug / fallback display. */
  message?: string
}

export const useJoinFlowStore = defineStore('joinFlow', () => {
  const invitationCode = ref<string>('')
  const householdPreview = ref<JoinHouseholdPreview | null>(null)
  const error = ref<JoinFlowError | null>(null)

  function setInvitationCode(code: string) {
    invitationCode.value = code.trim().toUpperCase()
  }

  function setHouseholdPreview(preview: JoinHouseholdPreview | null) {
    householdPreview.value = preview
  }

  function setError(err: JoinFlowError | null) {
    error.value = err
  }

  function clearError() {
    error.value = null
  }

  function reset() {
    invitationCode.value = ''
    householdPreview.value = null
    error.value = null
  }

  return {
    invitationCode,
    householdPreview,
    error,
    setInvitationCode,
    setHouseholdPreview,
    setError,
    clearError,
    reset,
  }
})

/**
 * Translates a raw error from acceptInvitation / network calls into a known
 * JoinFlowErrorKind. Used by login/register pages so they all map errors
 * the same way before bouncing the user back to /join/code.
 */
export function classifyJoinError(err: unknown): JoinFlowError {
  // Network failures (offline, fetch aborted, DNS, etc.)
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { kind: 'connection', message: 'offline' }
  }
  const e = err as { message?: string; code?: string; name?: string }
  const message = (e?.message || '').toLowerCase()
  if (
    e?.name === 'TypeError' && message.includes('fetch') ||
    message.includes('network') ||
    message.includes('failed to fetch')
  ) {
    return { kind: 'connection', message: e?.message }
  }
  // "already a member" is NOT the same as "used": the code may still be pending,
  // but THIS user already belongs to the household (e.g. the inviter scanning
  // their own code). They should be sent into the app, not shown a retry screen.
  // Le plafond d'échecs de preview_household_invitation. Il se dit avant tout
  // le reste : ce n'est pas le code qui est en cause, et proposer de le
  // corriger serait envoyer quelqu'un réparer ce qui n'est pas cassé.
  if (message.includes('too many attempts')) {
    return { kind: 'rate_limited', message: e?.message }
  }
  if (message.includes('already a member')) {
    return { kind: 'already_member', message: e?.message }
  }
  // Le foyer d'accueil ne porte qu'un parent. Normalement le code n'aurait pas
  // dû être émis (create_household_invitation refuse aussi), mais un code émis
  // avant l'expiration d'un abonnement arrive jusqu'ici. Celui qui le scanne
  // n'y peut rien : l'écran doit le renvoyer vers celui qui l'a invité.
  if (message.includes('premium_required_for_parent')) {
    return { kind: 'premium_required', message: e?.message }
  }
  if (message.includes('already accepted') || message.includes('already used')) {
    return { kind: 'used', message: e?.message }
  }
  if (message.includes('expired')) {
    return { kind: 'expired', message: e?.message }
  }
  if (message.includes('invalid') || message.includes('not found')) {
    return { kind: 'invalid', message: e?.message }
  }
  return { kind: 'unknown', message: e?.message }
}
